import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, fieldErrors, unwrap } from '../../api/client';
import type { Location } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { AssetLink } from '../../components/assets/AssetLink';
import { Badge, Button, Card, CardHeader, Checkbox, Dialog, EmptyState, ErrorBox, Field, Input, Loading, nullIfEmpty, PageHeader, StatusBadge, Table, Td, Textarea, Th, Tr, useToast } from '../../components/ui';

export function LocationListPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    queryKey: ['locations', 'list', showInactive],
    queryFn: () => unwrap(api.GET('/api/locations', { params: { query: { active: showInactive ? undefined : 'true', page_size: 200 } } })),
  });

  return (
    <>
      <PageHeader
        title="Standorte"
        actions={
          <>
            <Checkbox label="Inaktive anzeigen" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            {can('masterdata:write') && (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                Standort anlegen
              </Button>
            )}
          </>
        }
      />
      <Card>
        {list.isPending ? (
          <Loading />
        ) : list.error ? (
          <ErrorBox error={list.error} className="m-4" />
        ) : list.data.data.length === 0 ? (
          <EmptyState title="Keine Standorte" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Code</Th>
                <Th>Beschreibung</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {list.data.data.map((l) => (
                <Tr key={l.id} onClick={() => navigate(`/locations/${l.id}`)}>
                  <Td className="font-medium text-brand-700">{l.name}</Td>
                  <Td className="font-mono">{l.code ?? '–'}</Td>
                  <Td>{l.description ?? '–'}</Td>
                  <Td>{l.is_active ? <Badge tone="green">Aktiv</Badge> : <Badge>Inaktiv</Badge>}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <LocationDialog location={null} open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

export function LocationDetailPage() {
  const { ref = '' } = useParams();
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const loc = useQuery({ queryKey: ['locations', 'detail', ref], queryFn: () => unwrap(api.GET('/api/locations/{ref}', { params: { path: { ref } } })) });
  const assets = useQuery({
    queryKey: ['assets', 'by-location', ref],
    queryFn: () => unwrap(api.GET('/api/assets', { params: { query: { location: ref, top_level: 'true', page_size: 200, status: 'active,in_stock,repair,retired,lost,unknown' } } })),
  });

  if (loc.isPending) return <Loading />;
  if (loc.error) return <ErrorBox error={loc.error} />;
  const l = loc.data;

  return (
    <>
      <PageHeader
        back={
          <Link to="/locations" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
            <ArrowLeft className="size-4" /> Standorte
          </Link>
        }
        title={l.name}
        subtitle={
          <span className="flex items-center gap-2">
            {l.code && <span className="font-mono">{l.code}</span>}
            {l.is_active ? <Badge tone="green">Aktiv</Badge> : <Badge>Inaktiv</Badge>}
            {l.description}
          </span>
        }
        actions={
          can('masterdata:write') && (
            <Button icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
              Bearbeiten
            </Button>
          )
        }
      />
      <Card>
        <CardHeader title="Geräte an diesem Standort" description="Hauptgeräte; eingebaute Komponenten stehen beim jeweiligen Gerät." />
        {assets.isPending ? (
          <Loading />
        ) : assets.error ? (
          <ErrorBox error={assets.error} className="m-4" />
        ) : assets.data.data.length === 0 ? (
          <EmptyState title="Keine Geräte" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Nr.</Th>
                <Th>Bezeichnung</Th>
                <Th>Typ</Th>
                <Th>Mitarbeiter</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {assets.data.data.map((a) => (
                <Tr key={a.id}>
                  <Td>
                    <AssetLink asset={a} />
                  </Td>
                  <Td>{a.name}</Td>
                  <Td>{a.asset_type.label}</Td>
                  <Td>{a.effective_employee?.short_code ?? '–'}</Td>
                  <Td>
                    <StatusBadge status={a.status} />
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <LocationDialog location={l} open={editing} onClose={() => setEditing(false)} />
    </>
  );
}

function LocationDialog({ location, open, onClose }: { location: Location | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: '', code: '', description: '', is_active: true });
  useEffect(() => {
    if (open) setF({ name: location?.name ?? '', code: location?.code ?? '', description: location?.description ?? '', is_active: location?.is_active ?? true });
  }, [open, location]);
  const m = useMutation({
    mutationFn: () => {
      const body = { name: f.name.trim(), code: nullIfEmpty(f.code), description: nullIfEmpty(f.description) };
      return location
        ? unwrap(api.PATCH('/api/locations/{ref}', { params: { path: { ref: location.id } }, body: { ...body, is_active: f.is_active } }))
        : unwrap(api.POST('/api/locations', { body }));
    },
    onSuccess: (l) => {
      void qc.invalidateQueries({ queryKey: ['locations'] });
      toast(location ? 'Gespeichert' : `Standort ${l.name} angelegt`);
      onClose();
      if (!location) navigate(`/locations/${l.id}`);
    },
  });
  const errs = fieldErrors(m.error);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={location ? 'Standort bearbeiten' : 'Standort anlegen'}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!f.name.trim()}>
            Speichern
          </Button>
        </>
      }
    >
      <Field label="Name" required error={errs.name}>
        {(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="z. B. Serverraum" />}
      </Field>
      <Field label="Code" error={errs.code} hint="Kurzbezeichnung, optional">
        {(p) => <Input {...p} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />}
      </Field>
      <Field label="Beschreibung">{(p) => <Textarea {...p} rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />}</Field>
      {location && <Checkbox label="Aktiv" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} />}
      <ErrorBox error={m.error} />
    </Dialog>
  );
}
